import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import {
  MAX_ADDITIONAL_QTY, MSG_ITEMS_NOT_ADDED, MSG_PRESS_ADD_AGAIN, MSG_QTY_OUT_OF_RANGE, MSG_SELECT_AN_ITEM,
  draftSignature, parseAdditionalQty, toDraftRows, type DraftRow, type ItemRow, type ServerDraftRow,
} from './stockQtyEditModel';
import './StockQtyEditDialog.css';

const FORM_NAME = 'Items';
const BASE = '/api/v1/inventory-items/stock-qty-edit';

type Storage = { id: string; name: string };

const errorMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? fallback;

// G5 Items_ListView_StockQtyEdit: ItemCode · Item · StockQty (read-only) · Additional Qty (editable) ·
// Specification · Vendor.
const ITEM_COLUMNS: AppColumn<ItemRow>[] = [
  { field: 'itemCode', header: 'ItemCode', width: 110 },
  { field: 'item', header: 'Item', width: 200 },
  { field: 'stockQty', header: 'StockQty', width: 90, type: 'number', align: 'right' },
  { field: 'additionalQty', header: 'Additional Qty', width: 120, type: 'number', align: 'right', edit: { type: 'number' } },
  { field: 'specification', header: 'Specification', width: 240 },
  { field: 'vendor', header: 'Vendor', width: 150 },
];

/**
 * Items ▸ Stock Qty Edit — the "Stock Qty Editor" popup of ALPACA (ItemStockQtyEditorController).
 * The ticked items are listed on top with an editable Additional Qty; ADD drafts one container per unit
 * (a future dbo.Distribution row with its LT#) in "Stock Added"; the lab fills VendorLT / ExpiryDate /
 * Storage and OK stores them as PendingConsume and adds their count to each item's StockQty.
 * Saved through the shared useSaveFlow like every other popup; every rule is enforced by the API.
 */
export function StockQtyEditDialog({ itemIds, onClose, onSaved }: {
  /** The ids ticked in the Items list. */
  itemIds: string[];
  onClose: () => void;
  /** After a successful save — close and refresh the list. */
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [initialQty, setInitialQty] = useState<Record<string, number>>({});
  const [storages, setStorages] = useState<Storage[]>([]);
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [draftQty, setDraftQty] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Keyed on the ids' VALUE: a caller passing a fresh array each render must not reload (and reset) the popup.
  const idsKey = itemIds.join(',');
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.post<{ data?: ItemRow[] }>(`${BASE}/items`, { ids: idsKey === '' ? [] : idsKey.split(',') }),
      api.get<{ data?: Storage[] }>(`${BASE}/storages`),
    ])
      .then(([i, s]) => {
        if (!alive) return;
        const list = i.data.data ?? [];
        setItems(list);
        setInitialQty(Object.fromEntries(list.map((r) => [r.id, r.additionalQty])));
        setStorages(s.data.data ?? []);
      })
      .catch(() => { if (alive) notify.error('Could not load the items.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [idsKey, notify]);

  // The popup holds work the automatic unsaved-changes tracker cannot see (grid cell editors), so it
  // declares its own dirty state (standing rule 15).
  const dirty = drafts.length > 0 || items.some((r) => r.additionalQty !== (initialQty[r.id] ?? 0));
  useUnsavedGuard(dirty);
  const close = () => { void confirmDiscardIfUnsaved().then((leave) => { if (leave) onClose(); }); };

  const storageIdByName = useMemo(() => new Map(storages.map((s) => [s.name, s.id])), [storages]);

  const commitQty = (row: ItemRow, value: string) => {
    const qty = parseAdditionalQty(value);
    if (qty === null) { notify.warning(MSG_QTY_OUT_OF_RANGE); return; }
    setItems((cur) => cur.map((r) => (r.id === row.id ? { ...r, additionalQty: qty } : r)));
  };

  // ADD — G5 AddItems_Execute. The ticked items are drafted again from scratch, so changing a quantity and
  // pressing ADD replaces the old containers instead of stacking numbers on top of them.
  const add = async () => {
    const chosen = items.filter((r) => ticked.has(r.id));
    if (chosen.length === 0) { notify.info(MSG_SELECT_AN_ITEM); return; }
    const request = chosen.map((r) => ({ id: r.id, additionalQty: r.additionalQty }));
    if (drafts.length > 0 && draftSignature(request) === draftSignature(
      Object.entries(draftQty).map(([id, additionalQty]) => ({ id, additionalQty })))) {
      notify.info('Items are already added.');
      return;
    }
    setAdding(true);
    try {
      const res = await api.post<{ data?: ServerDraftRow[] }>(`${BASE}/draft`, { items: request });
      const rows = toDraftRows(res.data.data ?? []);
      if (rows.length === 0) { notify.info(MSG_ITEMS_NOT_ADDED); return; }
      setDrafts(rows);
      setDraftQty(Object.fromEntries(request.filter((r) => r.additionalQty > 0).map((r) => [r.id, r.additionalQty])));
    } catch (e) {
      notify.error(errorMessage(e, 'Could not add the items.'));
    } finally {
      setAdding(false);
    }
  };

  const commitDraft = (row: DraftRow, field: string, value: string) => {
    setDrafts((cur) => cur.map((r) => {
      if (r.key !== row.key) return r;
      if (field === 'vendorLT') return { ...r, vendorLT: value };
      if (field === 'expiry') return { ...r, expiry: value };
      if (field === 'storage') return { ...r, storage: value };
      return r;
    }));
  };

  const draftColumns: AppColumn<DraftRow>[] = useMemo(() => [
    { field: 'itemCode', header: 'ItemCode', width: 110 },
    { field: 'item', header: 'Item', width: 200 },
    { field: 'vendor', header: 'Vendor', width: 150 },
    { field: 'lt', header: 'LT#', width: 120 },
    { field: 'vendorLT', header: 'VendorLT', width: 130,
      edit: { type: 'text', requiredEmpty: (r) => r.isVendorLT && r.vendorLT.trim() === '' } },
    { field: 'expiry', header: 'ExpiryDate', width: 130, type: 'date',
      edit: { type: 'date', requiredEmpty: (r) => r.expiry === '' } },
    { field: 'storage', header: 'Storage', width: 140,
      edit: { type: 'combo', options: storages.map((s) => s.name), requiredEmpty: (r) => r.storage === '' } },
  ], [storages]);

  const save = () => {
    if (drafts.length === 0) { notify.info(MSG_ITEMS_NOT_ADDED); return; }
    // The containers must be the ones ADD drafted for the quantities now typed.
    const stale = items.some((r) => (draftQty[r.id] ?? 0) !== 0 && draftQty[r.id] !== r.additionalQty);
    if (stale) { notify.warning(MSG_PRESS_ADD_AGAIN); return; }
    const drafted = items.filter((r) => draftQty[r.id] !== undefined);
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        await api.post(`${BASE}/save`, {
          items: drafted.map((r) => ({ id: r.id, additionalQty: r.additionalQty })),
          rows: drafts.map((d) => ({
            itemId: d.itemId,
            vendorLT: d.vendorLT.trim() === '' ? null : d.vendorLT,
            expiryDate: d.expiry === '' ? null : d.expiry,
            storageId: storageIdByName.get(d.storage) ?? null,
          })),
        });
      },
      onSaved,
      onError: (e) => notify.error(errorMessage(e, 'Could not save the stock.')),
    });
  };

  return (
    <AppDialog
      open
      title="Stock Qty Editor"
      width={980}
      className="dlg-center inv-24 sqe-dlg"
      maximizable
      onClose={close}
      submitOnEnter={false}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={save}>OK</button>
          <button type="button" className="ca-btn" onClick={close}>Cancel</button>
        </>
      }
    >
      <div className="sqe-head">
        <h4 className="sqe-sec">Items</h4>
        <button type="button" className="ca-btn" disabled={loading || adding} onClick={() => void add()}>
          <span className="tb-i tb-i-green">＋</span> ADD
        </button>
      </div>
      <AppDataGrid<ItemRow>
        rows={items}
        columns={ITEM_COLUMNS}
        getRowId={(r) => r.id}
        loading={loading}
        defaultPageSize={10}
        selectable
        selectedIds={ticked}
        onSelectionChange={setTicked}
        onCellCommit={(row, col, value) => { if (col.field === 'additionalQty') commitQty(row, value); }}
        emptyText="No items"
      />
      <h4 className="sqe-sec sqe-sec-gap">Stock Added</h4>
      <AppDataGrid<DraftRow>
        rows={drafts}
        columns={draftColumns}
        getRowId={(r) => r.key}
        defaultPageSize={10}
        onCellCommit={(row, col, value) => commitDraft(row, String(col.field), value)}
        emptyText="No data to display"
      />
      <p className="sqe-hint">
        Tick the items, set Additional Qty and press ADD. Fill VendorLT, ExpiryDate and Storage, then OK adds the
        containers to the stock. Additional Qty can be 0 to {MAX_ADDITIONAL_QTY.toLocaleString('en-US')}.
      </p>
    </AppDialog>
  );
}
